import React, { useRef, useState } from "react";
import { Box, IconButton, Paper, Slider, Typography, alpha } from "@mui/material";
import { Pause, PlayArrow } from "@mui/icons-material";
import { useThemeContext } from "../../../../core/contexts/ThemeContext";
import { cores, raio, superficieModo, texto } from "../identidade";

interface AudioBubbleProps {
    audioUrl: string;
    timestamp: string;
    position: "left" | "right";
    color?: string;
}

const formatarDuracao = (segundos: number): string => {
    if (isNaN(segundos) || !isFinite(segundos)) return "0:00";
    const m = Math.floor(segundos / 60);
    const s = Math.floor(segundos % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
};

const AudioBubble: React.FC<AudioBubbleProps> = ({ audioUrl, timestamp, position, color }) => {
    const isRight = position === "right";
    const { theme, themeName } = useThemeContext();

    // Mesmo par de tons das bolhas de texto: tint da marca na loja, overlay
    // neutro no cliente.
    const bgColor = color || (isRight
        ? alpha(cores.marca[500], themeName === "dark" ? 0.22 : 0.14)
        : superficieModo(themeName, themeName === "dark" ? 0.07 : 0.05));

    const corBorda = isRight
        ? alpha(cores.marca[500], themeName === "dark" ? 0.42 : 0.24)
        : superficieModo(themeName, 0.09);

    const audioRef = useRef<HTMLAudioElement>(null);
    const [tocando, setTocando] = useState(false);
    const [progresso, setProgresso] = useState(0);
    const [duracao, setDuracao] = useState(0);
    const [tempoAtual, setTempoAtual] = useState(0);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (tocando) {
            audio.pause();
        } else {
            audio.play();
        }
        setTocando(!tocando);
    };

    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (!audio) return;
        setTempoAtual(audio.currentTime);
        setProgresso(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    };

    const handleLoadedMetadata = () => {
        const audio = audioRef.current;
        if (!audio) return;
        setDuracao(audio.duration);
    };

    const handleEnded = () => {
        setTocando(false);
        setProgresso(0);
        setTempoAtual(0);
        if (audioRef.current) audioRef.current.currentTime = 0;
    };

    const handleSliderChange = (_: Event, value: number | number[]) => {
        const audio = audioRef.current;
        if (!audio || !audio.duration) return;
        const newTime = ((value as number) / 100) * audio.duration;
        audio.currentTime = newTime;
        setTempoAtual(newTime);
        setProgresso(value as number);
    };

    return (
        <Box
            display="flex"
            justifyContent={isRight ? "flex-end" : "flex-start"}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    maxWidth: "82%",
                    minWidth: 220,
                    backgroundColor: bgColor,
                    border: `1px solid ${corBorda}`,
                    backgroundImage: "none",
                    position: "relative",
                    borderRadius: raio.bolha,
                }}
            >
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleEnded}
                />

                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                        onClick={togglePlay}
                        size="small"
                        sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.15),
                            "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.3),
                            },
                            flexShrink: 0,
                        }}
                    >
                        {tocando ? (
                            <Pause fontSize="small" color="primary" />
                        ) : (
                            <PlayArrow fontSize="small" color="primary" />
                        )}
                    </IconButton>

                    <Box flex={1}>
                        <Slider
                            value={progresso}
                            onChange={handleSliderChange}
                            size="small"
                            sx={{
                                py: 0,
                                color: theme.palette.primary.main,
                                "& .MuiSlider-thumb": { width: 10, height: 10 },
                            }}
                        />
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" sx={{ opacity: 0.6, fontSize: "0.7rem" }}>
                                {formatarDuracao(tempoAtual)}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.6, fontSize: "0.7rem" }}>
                                {formatarDuracao(duracao)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        textAlign: "right",
                        mt: 0.5,
                        opacity: 0.6,
                        ...texto.apoio,
                    }}
                >
                    {timestamp}
                </Typography>
            </Paper>
        </Box>
    );
};

export default AudioBubble;
